import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { Controller, useForm } from 'react-hook-form';
import { FileUploader } from './components/file-uploader';
import { RefundAmountExceededDialog } from './components/refund-amount-exceeded-dialog';
import {
  monthsOptions,
  paymentReceiptsOptions,
  pixKeyTypeOptions,
  purchaseReceiptsOptions,
} from './constants';
import { PixKeyType, TypeOfBankTransfer } from './interfaces';
import { formValidationSchema } from './schema';
import { DatePicker } from '@/components/shared/date-picker';
import { MaskedInput } from '@/components/shared/masked-input';
import { MoneyInput } from '@/components/shared/money-input';
import { useWizard } from '@/context/wizard';
import { bankOptions } from '@/data/@consts/banks';
import { SupplierType } from '@/data/subsidy/interfaces';
import { PriceTypes } from '@/models/catalog/course';
import { useRefundRequest } from '@/packages/subsidy/pages/refund-request/context';
import { InvoiceWizardStepIds } from '@/packages/subsidy/pages/refund-request/steps/invoice-attachment/interfaces';

const getErrorMessage = (
  error: { message?: string } | undefined
): string | undefined => error?.message;

const parseDate = (dateString: string) => {
  if (!dateString) return null;

  const parsedDate = DateTime.fromFormat(dateString, 'dd/MM/yyyy', {
    locale: 'pt-BR',
  });

  return parsedDate.isValid ? parsedDate : null;
};

const getPixKeyPlaceholder = (pixType?: PixKeyType) => {
  const placeholderOptions: Record<PixKeyType, string> = {
    [PixKeyType.Cpf]: '000.000.000-00',
    [PixKeyType.Email]: 'email@exemplo.com',
    [PixKeyType.Phone]: '+5511999999999',
    [PixKeyType.Random]: '00000000-0000-0000-0000-000000000000',
  };
  return pixType ? placeholderOptions[pixType] : 'Informe a chave PIX';
};

export const InvoiceForm = () => {
  const [showRefundAmountExceededDialog, setShowRefundAmountExceededDialog] =
    useState(false);
  const {
    values,
    actions: { navigate, updateValues },
  } = useWizard();
  const { resourceApproval } = useRefundRequest();
  const { control, watch, handleSubmit, trigger, reset, setValue } = useForm({
    defaultValues: {
      supplier_type: SupplierType.LegalPerson,
      bank_transfer_type: TypeOfBankTransfer.Pix,
      pix_key_type: PixKeyType.Cpf,
      reference_month: undefined,
    },
    resolver: zodResolver(formValidationSchema),
    mode: 'onChange',
  });

  const supplierType = watch('supplier_type');
  const bankTransferType = watch('bank_transfer_type');
  const pixKeyType = watch('pix_key_type');
  const receiverDocument = watch('receiver_fiscal_document');
  const purchaseAmount = watch('purchase_amount');
  const isTuition = resourceApproval?.sale_type === PriceTypes.Tuition;

  const subsidyPolicy = resourceApproval?.policies[0];
  const salePrice = parseFloat(resourceApproval?.sale_price ?? '0');

  const shouldShowRefundAmountExceededDialog =
    !subsidyPolicy?.configuration?.allow_refund_request_amount_override &&
    !subsidyPolicy?.limits?.amount_limit_per_purchase_cents &&
    isTuition &&
    purchaseAmount > salePrice;

  useEffect(() => {
    if (values.data) {
      reset(values.data);
    }
  }, [values.data, reset]);

  const onSubmit = handleSubmit((data) => {
    if (shouldShowRefundAmountExceededDialog) {
      setShowRefundAmountExceededDialog(true);
      return;
    }

    const normalizedData =
      bankTransferType === TypeOfBankTransfer.Pix &&
      pixKeyType === PixKeyType.Cpf
        ? { ...data, pix_key: data.receiver_fiscal_document }
        : data;

    updateValues({ data: normalizedData });
    navigate(InvoiceWizardStepIds.Summary);
  });

  return (
    <Stack gap={3}>
      <Stack gap={1}>
        <Typography variant="h3" color="primary.dark">
          Dados do fornecedor
        </Typography>
        <Typography>É a empresa ou pessoa que fez a venda do item</Typography>
      </Stack>

      <Controller
        control={control}
        name="supplier_name"
        render={({ field, fieldState: { error } }) => (
          <FormControl error={!!error} fullWidth>
            <FormLabel htmlFor="supplier_name">Nome do fornecedor</FormLabel>
            <TextField
              {...field}
              id="supplier_name"
              error={!!error}
              placeholder="Nome da empresa"
              helperText={getErrorMessage(error)}
            />
          </FormControl>
        )}
      />

      <Controller
        control={control}
        name="supplier_type"
        render={({ field, fieldState: { error } }) => (
          <FormControl
            component="fieldset"
            error={!!error}
            sx={{ width: 'fit-content' }}
          >
            <FormLabel component="legend" id="supplier_type-label">
              Tipo de fornecedor
            </FormLabel>
            <RadioGroup
              {...field}
              aria-labelledby="supplier_type-label"
              onChange={(e) => {
                field.onChange(e.target.value);
                trigger('supplier_document');
              }}
            >
              <FormControlLabel
                control={<Radio size="small" />}
                label={<Typography>Empresa / Pessoa jurídica</Typography>}
                value={SupplierType.LegalPerson}
              />
              <FormControlLabel
                control={<Radio size="small" />}
                label={<Typography>Pessoa física</Typography>}
                value={SupplierType.NaturalPerson}
              />
              <FormControlLabel
                control={<Radio size="small" />}
                label={<Typography>Compra internacional</Typography>}
                value={SupplierType.InternationalSupplier}
              />
            </RadioGroup>
            <FormHelperText>{getErrorMessage(error)}</FormHelperText>
          </FormControl>
        )}
      />

      <Stack direction={{ md: 'row' }}>
        <Controller
          control={control}
          name="supplier_document"
          render={({ field, fieldState: { error } }) => (
            <FormControl
              error={!!error}
              sx={{ flex: 1 }}
              disabled={supplierType === SupplierType.InternationalSupplier}
            >
              <FormLabel htmlFor="supplier_document">
                {supplierType === SupplierType.LegalPerson ? 'CNPJ' : 'CPF'} do
                fornecedor
              </FormLabel>
              <MaskedInput
                {...field}
                id="supplier_document"
                maskType={
                  supplierType === SupplierType.LegalPerson ? 'cnpj' : 'cpf'
                }
                error={!!error}
                disabled={supplierType === SupplierType.InternationalSupplier}
                placeholder={
                  supplierType === SupplierType.LegalPerson
                    ? '00.000.000/0000-00'
                    : '000.000.000-00'
                }
              />
              <FormHelperText>{getErrorMessage(error)}</FormHelperText>
            </FormControl>
          )}
        />
        <Box flex={1} display={{ xs: 'none', md: 'block' }} />
      </Stack>

      <Divider />

      <Stack spacing={1}>
        <Typography variant="h3" color="primary.dark">
          Comprovante de compra
        </Typography>
        <Typography>
          É o documento que o fornecedor emite após confirmação do pagamento.
          Pode ser uma nota fiscal, boleto ou recibo. Para compras
          internacionais você pode usar invoice.
        </Typography>
      </Stack>

      <Stack direction={{ md: 'row' }}>
        <Controller
          control={control}
          name="receiver_fiscal_document"
          render={({ field, fieldState: { error } }) => (
            <FormControl error={!!error} sx={{ flex: 1 }}>
              <FormLabel htmlFor="receiver_fiscal_document">
                CPF do comprador
              </FormLabel>
              <MaskedInput
                {...field}
                id="receiver_fiscal_document"
                maskType="cpf"
                error={!!error}
                placeholder="012.345.678-90"
              />
              <FormHelperText>{getErrorMessage(error)}</FormHelperText>
            </FormControl>
          )}
        />
        <Box flex={1} display={{ xs: 'none', md: 'block' }} />
      </Stack>

      <Stack direction={{ md: 'row' }} alignItems={{ md: 'start' }} gap={2}>
        <Controller
          control={control}
          name="purchase_receipt_type"
          render={({
            field: { onChange, value, ...field },
            fieldState: { error },
          }) => (
            <FormControl sx={{ flex: 1 }} error={!!error}>
              <FormLabel
                htmlFor="purchase_receipt_type"
                id="purchase_receipt_type-label"
              >
                Tipo de comprovante
              </FormLabel>
              <Autocomplete
                {...field}
                id="purchase_receipt_type"
                options={purchaseReceiptsOptions}
                getOptionLabel={(item) => item.label}
                getOptionKey={(item) => item.value}
                value={
                  purchaseReceiptsOptions?.find(
                    (option) => option.value === value
                  ) ?? null
                }
                onChange={(_, newValue) => {
                  onChange(newValue?.value ?? null);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Selecione"
                    error={!!error}
                    helperText={getErrorMessage(error)}
                  />
                )}
              />
            </FormControl>
          )}
        />

        <Controller
          control={control}
          name="purchase_date"
          render={({ field, fieldState: { error } }) => (
            <FormControl sx={{ flex: 1 }} error={!!error}>
              <FormLabel id="purchase_date-label" htmlFor="purchase_date">
                Data da compra
              </FormLabel>
              <DatePicker
                field={field}
                value={parseDate(field.value)}
                aria-labelledby="purchase_date-label"
                slotProps={{
                  textField: {
                    error: !!error,
                    helperText: getErrorMessage(error),
                  },
                }}
              />
            </FormControl>
          )}
        />

        <Controller
          control={control}
          name="purchase_amount"
          render={({ field, fieldState: { error } }) => (
            <FormControl error={!!error} sx={{ flex: 1 }}>
              <FormLabel htmlFor="purchase_amount">
                Valor do comprovante (R$)
              </FormLabel>
              <TextField
                {...field}
                id="purchase_amount"
                error={!!error}
                placeholder="R$ 0,00"
                slotProps={{
                  input: {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    inputComponent: MoneyInput as any,
                    startAdornment: (
                      <InputAdornment disableTypography position="start">
                        R$
                      </InputAdornment>
                    ),
                  },
                }}
                helperText={getErrorMessage(error)}
              />
            </FormControl>
          )}
        />
      </Stack>

      <Stack direction={{ md: 'row' }}>
        <Controller
          control={control}
          name="purchase_attachments"
          render={({ field, fieldState: { error } }) => (
            <FormControl error={!!error} sx={{ flex: 1 }}>
              <FormLabel component="legend" id="purchase_attachments-label">
                Anexos
              </FormLabel>
              <FileUploader
                id="purchase_attachments"
                labelId="purchase_attachments-label"
                onChange={field.onChange}
                value={field.value}
                error={!!error}
                helperText={getErrorMessage(error)}
              />
            </FormControl>
          )}
        />
        <Box flex={1} display={{ xs: 'none', md: 'block' }} />
      </Stack>

      <Divider />

      <Stack spacing={1}>
        <Typography variant="h3" color="primary.dark">
          Comprovante de pagamento
        </Typography>
        <Typography>
          É o documento emitido pelo banco comprovando o pagamento. Pode ser a
          fatura do cartão ou imagem do comprovante no aplicativo bancário. Só
          serão aceitos documentos que possuam data da compra, valor e nome do
          pagador.
        </Typography>
      </Stack>

      <Stack direction={{ md: 'row' }} alignItems={{ md: 'start' }} gap={2}>
        <Controller
          control={control}
          name="payment_receipt_type"
          render={({
            field: { onChange, value, ...field },
            fieldState: { error },
          }) => (
            <FormControl sx={{ flex: 1 }} error={!!error}>
              <FormLabel
                id="payment_receipt_type-label"
                htmlFor="payment_receipt_type"
              >
                Tipo de comprovante
              </FormLabel>
              <Autocomplete
                {...field}
                id="payment_receipt_type"
                options={paymentReceiptsOptions}
                getOptionLabel={(item) => item.label}
                getOptionKey={(item) => item.value}
                value={
                  paymentReceiptsOptions?.find(
                    (option) => option.value === value
                  ) ?? null
                }
                onChange={(_, newValue) => {
                  onChange(newValue?.value ?? null);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Selecione"
                    error={!!error}
                    helperText={getErrorMessage(error)}
                  />
                )}
              />
            </FormControl>
          )}
        />

        <Controller
          control={control}
          name="payment_date"
          render={({ field, fieldState: { error } }) => (
            <FormControl sx={{ flex: 1 }} error={!!error}>
              <FormLabel htmlFor="payment_date">Data do pagamento</FormLabel>
              <DatePicker
                field={field}
                value={parseDate(field.value)}
                slotProps={{
                  textField: {
                    error: !!error,
                    helperText: getErrorMessage(error),
                  },
                }}
              />
            </FormControl>
          )}
        />

        <Controller
          control={control}
          name="payment_amount"
          render={({ field, fieldState: { error } }) => (
            <FormControl error={!!error} sx={{ flex: 1 }}>
              <FormLabel htmlFor="payment_amount">
                Valor do comprovante (R$)
              </FormLabel>
              <TextField
                {...field}
                id="payment_amount"
                error={!!error}
                placeholder="R$ 0,00"
                slotProps={{
                  input: {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    inputComponent: MoneyInput as any,
                    startAdornment: (
                      <InputAdornment disableTypography position="start">
                        R$
                      </InputAdornment>
                    ),
                  },
                }}
                helperText={getErrorMessage(error)}
              />
            </FormControl>
          )}
        />
      </Stack>
      {isTuition && (
        <Stack direction={{ md: 'row' }}>
          <Controller
            control={control}
            name="reference_month"
            render={({ field, fieldState: { error } }) => (
              <FormControl error={!!error} sx={{ flex: 1 }}>
                <FormLabel id="reference_month-label" htmlFor="reference_month">
                  Mês de referência
                </FormLabel>
                <Autocomplete
                  {...field}
                  id="reference_month"
                  options={monthsOptions}
                  getOptionLabel={(item) => item.label}
                  getOptionKey={(item) => item.value}
                  value={
                    monthsOptions?.find(
                      (option) => option.value === field.value
                    ) ?? null
                  }
                  onChange={(_, newValue) => {
                    field.onChange(newValue?.value ?? null);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Selecione"
                      error={!!error}
                      helperText={getErrorMessage(error)}
                    />
                  )}
                />
              </FormControl>
            )}
          />
          <Box flex={1} display={{ xs: 'none', md: 'block' }} />
        </Stack>
      )}

      <Stack direction={{ md: 'row' }}>
        <Controller
          control={control}
          name="payment_attachments"
          render={({ field, fieldState: { error } }) => (
            <FormControl error={!!error} sx={{ flex: 1 }}>
              <FormLabel component="legend" id="payment_attachments-label">
                Anexos
              </FormLabel>
              <FileUploader
                id="payment_attachments"
                labelId="payment_attachments-label"
                onChange={field.onChange}
                value={field.value}
                error={!!error}
                helperText={getErrorMessage(error)}
              />
            </FormControl>
          )}
        />
        <Box flex={1} display={{ xs: 'none', md: 'block' }} />
      </Stack>

      <Divider />

      <Stack spacing={1}>
        <Typography variant="h3" color="primary.dark">
          Outros comprovantes (Opcional)
        </Typography>
        <Typography>
          Utilize esse espaço para adicionar comprovantes adicionais quando
          solicitados.
        </Typography>
      </Stack>

      <Stack direction={{ md: 'row' }}>
        <Controller
          control={control}
          name="optional_files"
          render={({ field, fieldState: { error } }) => (
            <FormControl error={!!error} sx={{ flex: 1 }}>
              <FileUploader
                id="optional_files"
                onChange={field.onChange}
                value={field.value}
                error={!!error}
                helperText={getErrorMessage(error)}
              />
            </FormControl>
          )}
        />
        <Box flex={1} display={{ xs: 'none', md: 'block' }} />
      </Stack>

      <Divider />

      <Stack spacing={1}>
        <Typography variant="h3" color="primary.dark">
          Dados bancários
        </Typography>
        <Typography>
          Informe os dados da conta onde deseja receber o reembolso. A conta
          precisa estar no seu nome.
        </Typography>
      </Stack>

      <Controller
        control={control}
        name="bank_transfer_type"
        render={({ field, fieldState: { error } }) => (
          <FormControl
            component="fieldset"
            error={!!error}
            sx={{ width: 'fit-content' }}
          >
            <FormLabel component="legend" id="bank_transfer_type-label">
              Como deseja receber seu reembolso?
            </FormLabel>
            <RadioGroup
              {...field}
              aria-labelledby="bank_transfer_type-label"
              onChange={(e) => {
                field.onChange(e.target.value);
                if (e.target.value === TypeOfBankTransfer.Pix) {
                  trigger(['pix_key_type', 'pix_key']);
                } else {
                  trigger(['bank', 'agency', 'account_number']);
                }
              }}
            >
              <FormControlLabel
                control={<Radio size="small" />}
                label={<Typography>PIX</Typography>}
                value={TypeOfBankTransfer.Pix}
              />
              <FormControlLabel
                control={<Radio size="small" />}
                label={<Typography>TED / Transferência Bancária</Typography>}
                value={TypeOfBankTransfer.Ted}
              />
            </RadioGroup>
            <FormHelperText>{getErrorMessage(error)}</FormHelperText>
          </FormControl>
        )}
      />

      <Stack direction={{ md: 'row' }} gap={2}>
        <Controller
          control={control}
          name="holder_name"
          render={({ field, fieldState: { error } }) => (
            <FormControl
              error={!!error}
              sx={{ width: { xs: '100%', md: '50%' } }}
            >
              <FormLabel htmlFor="holder_name">Nome completo</FormLabel>
              <TextField
                {...field}
                id="holder_name"
                error={!!error}
                placeholder="Nome Completo"
                helperText={getErrorMessage(error)}
              />
            </FormControl>
          )}
        />
      </Stack>

      {bankTransferType === TypeOfBankTransfer.Pix && (
        <>
          <Controller
            control={control}
            name="pix_key_type"
            render={({ field, fieldState: { error } }) => (
              <FormControl
                component="fieldset"
                error={!!error}
                sx={{ width: 'fit-content' }}
              >
                <FormLabel component="legend" id="pix_key_type-label">
                  Tipo de chave PIX
                </FormLabel>
                <RadioGroup
                  {...field}
                  aria-labelledby="pix_key_type-label"
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    setValue('pix_key', '');
                    trigger('pix_key');
                  }}
                >
                  {pixKeyTypeOptions.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      control={<Radio size="small" />}
                      label={<Typography>{option.label}</Typography>}
                      value={option.value}
                    />
                  ))}
                </RadioGroup>
                <FormHelperText>{getErrorMessage(error)}</FormHelperText>
              </FormControl>
            )}
          />

          <Stack direction={{ md: 'row' }}>
            <Controller
              control={control}
              name="pix_key"
              render={({ field, fieldState: { error } }) => (
                <FormControl
                  error={!!error}
                  sx={{ flex: 1 }}
                  disabled={pixKeyType === PixKeyType.Cpf}
                >
                  <FormLabel htmlFor="pix_key">Chave PIX</FormLabel>
                  {pixKeyType === PixKeyType.Cpf ? (
                    <TextField
                      id="pix_key"
                      value={receiverDocument || ''}
                      disabled
                      placeholder={getPixKeyPlaceholder(pixKeyType)}
                    />
                  ) : pixKeyType === PixKeyType.Phone ? (
                    <MaskedInput
                      {...field}
                      id="pix_key"
                      maskType="phone"
                      error={!!error}
                      placeholder={getPixKeyPlaceholder(pixKeyType)}
                    />
                  ) : (
                    <TextField
                      {...field}
                      id="pix_key"
                      error={!!error}
                      placeholder={getPixKeyPlaceholder(pixKeyType)}
                    />
                  )}

                  {pixKeyType === PixKeyType.Cpf ? (
                    <FormHelperText>
                      Por segurança, a chave Pix será o CPF do comprador.
                    </FormHelperText>
                  ) : (
                    <FormHelperText>{getErrorMessage(error)}</FormHelperText>
                  )}
                </FormControl>
              )}
            />
            <Box flex={1} display={{ xs: 'none', md: 'block' }} />
          </Stack>
        </>
      )}

      {bankTransferType === TypeOfBankTransfer.Ted && (
        <>
          <Controller
            control={control}
            name="bank"
            render={({
              field: { onChange, value, ...field },
              fieldState: { error },
            }) => (
              <FormControl error={!!error} fullWidth>
                <FormLabel id="bank-label" htmlFor="bank">
                  Banco
                </FormLabel>
                <Autocomplete
                  {...field}
                  id="bank"
                  options={bankOptions}
                  getOptionLabel={(item) => item.label}
                  getOptionKey={(item) => item.value}
                  value={
                    bankOptions?.find((option) => option.value === value) ??
                    null
                  }
                  onChange={(_, newValue) => {
                    onChange(newValue?.value ?? '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Selecione"
                      error={!!error}
                      helperText={getErrorMessage(error)}
                    />
                  )}
                />
              </FormControl>
            )}
          />

          <Stack direction={{ md: 'row' }} gap={2}>
            <Controller
              control={control}
              name="agency"
              render={({ field, fieldState: { error } }) => (
                <FormControl error={!!error} sx={{ flex: 1 }}>
                  <FormLabel htmlFor="agency">Agência</FormLabel>
                  <TextField
                    {...field}
                    id="agency"
                    error={!!error}
                    placeholder="0001"
                    helperText={getErrorMessage(error)}
                  />
                </FormControl>
              )}
            />

            <Controller
              control={control}
              name="account_number"
              render={({ field, fieldState: { error } }) => (
                <FormControl error={!!error} sx={{ flex: 1 }}>
                  <FormLabel htmlFor="account_number">
                    Número da conta
                  </FormLabel>
                  <TextField
                    {...field}
                    id="account_number"
                    error={!!error}
                    placeholder="12345-6"
                    helperText={getErrorMessage(error)}
                  />
                </FormControl>
              )}
            />
          </Stack>
        </>
      )}

      <Stack direction="row" justifyContent="flex-end" mt={4}>
        <Button onClick={onSubmit} variant="contained">
          Próximo
        </Button>
      </Stack>

      {showRefundAmountExceededDialog && (
        <RefundAmountExceededDialog
          onClose={() => setShowRefundAmountExceededDialog(false)}
          purchaseAmount={purchaseAmount}
        />
      )}
    </Stack>
  );
};
